import { useId, type KeyboardEvent, type ReactNode } from 'react'
import { useDrawerParam } from './useDrawerParam'
import './Module.css'

interface ModuleProps {
  title: string
  caption?: string
  drawerKey: string
  span: number
  isLoading?: boolean
  isError?: boolean
  errorText?: string
  isEmpty?: boolean
  emptyText?: string
  className?: string
  children: ReactNode
}

/**
 * Summary card on the desktop Fleet grid. The whole surface is one
 * button-like target that opens its drawer via `?panel=<drawerKey>`.
 * Body ladder: loading > error > empty > children.
 */
export function Module({
  title,
  caption,
  drawerKey,
  span,
  isLoading,
  isError,
  errorText,
  isEmpty,
  emptyText,
  className,
  children,
}: ModuleProps) {
  const { open } = useDrawerParam()
  const titleId = useId()
  const bodyId = useId()

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.repeat || e.target !== e.currentTarget) return
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      open(drawerKey)
    }
  }

  let body: ReactNode = children
  if (isLoading) {
    body = <div className="module-skeleton" aria-busy="true" />
  } else if (isError) {
    body = <p className="module-state">{errorText ?? 'unavailable'}</p>
  } else if (isEmpty) {
    body = <p className="module-state">{emptyText ?? 'no data'}</p>
  }

  return (
    <div
      role="button"
      tabIndex={0}
      aria-labelledby={titleId}
      aria-describedby={bodyId}
      aria-haspopup="dialog"
      className={`module card focus-ring${className ? ` ${className}` : ''}`}
      style={{ ['--span' as string]: span }}
      data-drawer-key={drawerKey}
      onClick={() => open(drawerKey)}
      onKeyDown={onKeyDown}
    >
      <div className="module-header">
        <h3 id={titleId} className="module-title">
          {title}
          {caption && <span className="module-caption">{caption}</span>}
        </h3>
        <span className="module-open" aria-hidden>
          open ›
        </span>
      </div>
      <div id={bodyId} className="module-body">{body}</div>
    </div>
  )
}
